
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, ArrowLeft, Save, Download, Shield } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

const PrivacyComplianceSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    gdprCompliance: {
      enabled: true,
      dataRetentionDays: 365,
      allowDataExport: true,
      allowDataDeletion: true,
      consentRequired: true
    },
    dataPrivacy: {
      enableEncryption: true,
      encryptionLevel: 'AES-256',
      anonymizeData: false,
      trackingOptOut: true
    },
    auditSettings: {
      enableAuditLogs: true,
      logRetentionDays: 90,
      logUserActions: true,
      logSystemChanges: true
    },
    complianceFrameworks: {
      gdpr: true,
      ccpa: false,
      hipaa: false,
      sox: false
    }
  });

  const handleSave = () => {
    toast.success("Privacy & compliance settings saved successfully");
  };

  const exportAuditLogs = () => {
    toast.success("Audit logs export initiated");
  };

  const generateComplianceReport = () => {
    toast.success("Compliance report generated successfully");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft className="h-4 w-4" />
          Back to System Settings
        </Button>
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Privacy & Compliance</h1>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              GDPR Compliance
            </CardTitle>
            <CardDescription>Configure General Data Protection Regulation compliance settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Enable GDPR Compliance</Label>
                <p className="text-sm text-muted-foreground">Activate GDPR compliance features</p>
              </div>
              <Switch
                checked={settings.gdprCompliance.enabled}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  gdprCompliance: {...settings.gdprCompliance, enabled: checked}
                })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataRetention">Data Retention (days)</Label>
                <Input
                  id="dataRetention"
                  type="number"
                  value={settings.gdprCompliance.dataRetentionDays}
                  onChange={(e) => setSettings({
                    ...settings,
                    gdprCompliance: {...settings.gdprCompliance, dataRetentionDays: parseInt(e.target.value)}
                  })}
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Allow Data Export</Label>
                <Switch
                  checked={settings.gdprCompliance.allowDataExport}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    gdprCompliance: {...settings.gdprCompliance, allowDataExport: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Data Deletion</Label>
                <Switch
                  checked={settings.gdprCompliance.allowDataDeletion}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    gdprCompliance: {...settings.gdprCompliance, allowDataDeletion: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Require User Consent</Label>
                <Switch
                  checked={settings.gdprCompliance.consentRequired}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    gdprCompliance: {...settings.gdprCompliance, consentRequired: checked}
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Privacy Settings</CardTitle>
            <CardDescription>Configure data encryption and privacy controls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Data Encryption</Label>
              <Switch
                checked={settings.dataPrivacy.enableEncryption}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  dataPrivacy: {...settings.dataPrivacy, enableEncryption: checked}
                })}
              />
            </div>
            
            <div>
              <Label htmlFor="encryptionLevel">Encryption Level</Label>
              <Select 
                value={settings.dataPrivacy.encryptionLevel} 
                onValueChange={(value) => setSettings({
                  ...settings,
                  dataPrivacy: {...settings.dataPrivacy, encryptionLevel: value}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AES-128">AES-128</SelectItem>
                  <SelectItem value="AES-256">AES-256</SelectItem>
                  <SelectItem value="RSA-2048">RSA-2048</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Anonymize User Data</Label>
              <Switch
                checked={settings.dataPrivacy.anonymizeData}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  dataPrivacy: {...settings.dataPrivacy, anonymizeData: checked}
                })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Allow Tracking Opt-out</Label>
              <Switch
                checked={settings.dataPrivacy.trackingOptOut}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  dataPrivacy: {...settings.dataPrivacy, trackingOptOut: checked}
                })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Audit Logs</CardTitle>
            <CardDescription>Configure system audit logging and monitoring</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Audit Logging</Label>
              <Switch
                checked={settings.auditSettings.enableAuditLogs}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  auditSettings: {...settings.auditSettings, enableAuditLogs: checked}
                })}
              />
            </div>
            
            <div>
              <Label htmlFor="logRetention">Log Retention (days)</Label>
              <Input
                id="logRetention"
                type="number"
                value={settings.auditSettings.logRetentionDays}
                onChange={(e) => setSettings({
                  ...settings,
                  auditSettings: {...settings.auditSettings, logRetentionDays: parseInt(e.target.value)}
                })}
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Log User Actions</Label>
                <Switch
                  checked={settings.auditSettings.logUserActions}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    auditSettings: {...settings.auditSettings, logUserActions: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Log System Changes</Label>
                <Switch
                  checked={settings.auditSettings.logSystemChanges}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    auditSettings: {...settings.auditSettings, logSystemChanges: checked}
                  })}
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={exportAuditLogs} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Audit Logs
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Compliance Frameworks</CardTitle>
            <CardDescription>Enable compliance with various regulatory frameworks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>GDPR (General Data Protection Regulation)</Label>
                  <p className="text-sm text-muted-foreground">European Union data protection</p>
                </div>
                <Switch
                  checked={settings.complianceFrameworks.gdpr}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    complianceFrameworks: {...settings.complianceFrameworks, gdpr: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>CCPA (California Consumer Privacy Act)</Label>
                  <p className="text-sm text-muted-foreground">California privacy regulations</p>
                </div>
                <Switch
                  checked={settings.complianceFrameworks.ccpa}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    complianceFrameworks: {...settings.complianceFrameworks, ccpa: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>HIPAA (Health Insurance Portability)</Label>
                  <p className="text-sm text-muted-foreground">Healthcare data protection</p>
                </div>
                <Switch
                  checked={settings.complianceFrameworks.hipaa}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    complianceFrameworks: {...settings.complianceFrameworks, hipaa: checked}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>SOX (Sarbanes-Oxley Act)</Label>
                  <p className="text-sm text-muted-foreground">Financial reporting compliance</p>
                </div>
                <Switch
                  checked={settings.complianceFrameworks.sox}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    complianceFrameworks: {...settings.complianceFrameworks, sox: checked}
                  })}
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={generateComplianceReport} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Generate Compliance Report
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyComplianceSettings;
